import { defineSlotRecipe } from '@chakra-ui/react';
import { datePickerAnatomy } from '@chakra-ui/react/anatomy';

import {
  datePickerCellHeightPx,
  datePickerCellWidthPx,
  datePickerHeaderControlHeightPx,
  datePickerHeaderSectionGapPx,
  datePickerNavIconSizePx,
  datePickerPopoverMinWidthPx,
  datePickerPopoverShadow,
} from './date-picker.kds';

/** KDS Date picker overrides — Figma node 260:4837. */
export const datePickerSlotRecipe = defineSlotRecipe({
  slots: datePickerAnatomy.keys(),
  base: {
    root: {
      width: 'full',
    },
    content: {
      bg: 'neutral.0',
      borderWidth: '1px',
      borderColor: 'neutral.0',
      borderRadius: 'md',
      boxShadow: datePickerPopoverShadow,
      p: '3',
      gap: '2',
      minW: '0',
    },
    positioner: {},
    view: {
      '--table-cell-size': `${datePickerCellWidthPx}px`,
      '--datepicker-nav-trigger-size': `${datePickerHeaderControlHeightPx}px`,
      '--datepicker-select-height': `${datePickerHeaderControlHeightPx}px`,
      gap: '4',
    },
    viewControl: {
      gap: `${datePickerHeaderSectionGapPx}px`,
      height: `${datePickerHeaderControlHeightPx}px`,
      justifyContent: 'flex-start',
      width: 'full',
    },
    prevTrigger: {
      borderRadius: 'md',
      color: 'neutral.500',
      minW: `${datePickerHeaderControlHeightPx}px`,
      w: `${datePickerHeaderControlHeightPx}px`,
      h: `${datePickerHeaderControlHeightPx}px`,
      _hover: {
        bg: 'transparent',
        color: 'neutral.500',
      },
      _icon: {
        boxSize: `${datePickerNavIconSizePx}px`,
      },
    },
    nextTrigger: {
      borderRadius: 'md',
      color: 'neutral.500',
      minW: `${datePickerHeaderControlHeightPx}px`,
      w: `${datePickerHeaderControlHeightPx}px`,
      h: `${datePickerHeaderControlHeightPx}px`,
      _hover: {
        bg: 'transparent',
        color: 'neutral.500',
      },
      _icon: {
        boxSize: `${datePickerNavIconSizePx}px`,
      },
    },
    table: {
      borderCollapse: 'separate',
      borderSpacing: '0 8px',
    },
    tableHeader: {
      textStyle: 'small.regular',
      color: 'neutral.250',
      fontWeight: 'normal',
      textTransform: 'none',
      width: `${datePickerCellWidthPx}px`,
      py: '0',
    },
    tableCell: {
      py: '0',
    },
    tableCellTrigger: {
      minWidth: `${datePickerCellWidthPx}px`,
      minHeight: `${datePickerCellHeightPx}px`,
      textStyle: 'default.regular',
      color: 'neutral.400',
      borderRadius: 'md',
      _hover: {
        bg: 'brand.100',
        color: 'brand.500',
      },
      _today: {
        bg: 'brand.100',
        color: 'brand.500',
        fontWeight: 'medium',
        textDecoration: 'none',
      },
      '&[data-selected]': {
        bg: 'brand.500',
        color: 'neutral.0',
        fontWeight: 'semibold',
        _hover: {
          bg: 'brand.500',
          color: 'neutral.0',
        },
      },
      _disabled: {
        opacity: 0.4,
      },
    },
    input: {
      textStyle: 'default.regular',
      borderColor: 'neutral.250',
      borderRadius: 'md',
      color: 'neutral.500',
      _placeholder: {
        color: 'neutral.250',
      },
    },
    trigger: {
      color: 'neutral.400',
      _hover: {
        color: 'neutral.500',
      },
      _icon: {
        boxSize: '20px',
      },
    },
  },
  variants: {
    size: {
      sm: {
        root: {
          '--datepicker-input-height': `${datePickerHeaderControlHeightPx}px`,
          '--datepicker-input-px': 'sizes.2',
          '--datepicker-indicators-offset': 'sizes.2',
        },
      },
      md: {
        root: {
          '--datepicker-input-height': 'sizes.10',
          '--datepicker-input-px': 'sizes.3',
        },
        view: {
          '--datepicker-nav-trigger-size': 'sizes.8',
          '--datepicker-select-height': 'sizes.10',
        },
      },
    },
    variant: {
      outline: {
        input: {
          bg: 'neutral.0',
          borderWidth: '1px',
          borderColor: 'neutral.250',
          focusVisibleRing: 'none',
          _hover: {
            borderColor: 'neutral.350',
          },
          _focus: {
            borderColor: 'brand.500',
            outline: 'none',
          },
          _invalid: {
            borderColor: 'red.700',
            _hover: { borderColor: 'red.700' },
            _focus: { borderColor: 'red.700' },
          },
          _disabled: {
            bg: 'neutral.50',
            borderColor: 'neutral.200',
            color: 'neutral.350',
            opacity: 1,
            cursor: 'not-allowed',
          },
        },
      },
    },
    matchAnchorWidth: {
      true: {
        content: {
          width: 'full',
        },
        positioner: {
          width: 'var(--reference-width)',
        },
      },
      false: {
        content: {
          minW: `${datePickerPopoverMinWidthPx}px`,
          width: 'auto',
        },
        positioner: {
          minW: `${datePickerPopoverMinWidthPx}px`,
          width: 'auto',
        },
      },
    },
  },
  defaultVariants: {
    size: 'sm',
    variant: 'outline',
    matchAnchorWidth: false,
  },
});
